import { UserOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  message,
  PageHeader,
  Popconfirm,
  Table,
  Tag,
  Typography,
} from "antd";
import "assets/fonticons/style.css";
import PdfLogo from "assets/images/pdflogo.png";
import LayoutCss from "layout/layout.module.scss";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CommissionPlan from "redux/models/commissionPlan";
import K from "utilities/constants";
import { convertIntoDashUSFormat } from "utilities/dateUtility";
import {
  checkNullPlaceHolder,
  isPermissionPresent,
  removeUnderScore,
} from "utilities/generalUtility";
import { displayDollar } from "utilities/tableUtility";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { selectUser } from "redux/slices/userSlice";
import { useSelector } from "react-redux";
import { keyBy, sumBy, uniqBy, groupBy } from "lodash";
import { numberSorter, stringSorter } from "utilities/sorting";
import { Tooltip } from "common/components/tooltip/tooltip";
import { getApprovalsTotal, getThresholdValue } from "./utils";
import styles from "../invoices/clientInvoices.module.scss";

const { Title, Paragraph } = Typography;

const tagColors = {
  SUCCESS: "success",
  PENDING: "blue",
};

const renderSummary = (data) => {
  const totalBilled = sumBy(data, "thresholdMaker");
  const totalCommission = sumBy(data, "duplicateAmount");

  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={4} />
        <Table.Summary.Cell index={4} align="center">
          <strong>Total ({data.length})</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="center">
          <strong>{displayDollar(totalBilled)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} align="center">
          <strong>{displayDollar(totalCommission)}</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
};

const columns = [
  {
    title: "ID",
    align: "center",
    dataIndex: "id",
    sorter: numberSorter("id"),
  },
  {
    title: "Candidate Name",
    align: "center",
    dataIndex: "submittedJobBill",
    render: (obj) => checkNullPlaceHolder(obj?.candidateName),
    defaultSortOrder: "ascend",
    sorter: stringSorter("submittedJobBill.candidateName"),
  },
  {
    title: "Candidate Type",
    align: "center",
    dataIndex: "submittedJobBill",
    render: (obj) =>
      K.CandidateType[obj?.candidateJobEntry?.hireType] || "Cancellation Fee",
  },
  {
    title: "Billing Type",
    dataIndex: "submittedJobBill",
    align: "center",
    ellipsis: {
      showTitle: false,
    },
    render: (obj) => {
      const text = removeUnderScore(obj?.billingTypeName);
      return (
        <Tooltip title={text} placement="right" showArrow={false}>
          <span
            style={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {text}
          </span>
        </Tooltip>
      );
    },
  },
  {
    title: "Candidate Start Date",
    dataIndex: "submittedJobBill",
    align: "center",
    render: (text) =>
      convertIntoDashUSFormat(text?.candidateJobEntry?.startDate, false),
  },
  {
    title: "Billed Amount",
    dataIndex: "thresholdMaker",
    align: "center",
    render: (text) => displayDollar(text),
  },
  {
    title: "Commission Earned",
    dataIndex: "duplicateAmount",
    align: "center",
    render: (text) => displayDollar(text),
  },
];

export default function CommissionStatementDetail() {
  const { id } = useParams();
  const history = useHistory();
  const [pageData, setPageData] = useState({
    details: null,
  });
  const { details } = pageData;
  const userSlice = useSelector(selectUser);

  const approvals = details?.commissionApprovals || [];

  const approvalsByClientId = groupBy(
    approvals,
    (a) => a.submittedJobBill.client.id,
  );

  const uniqueClients = uniqBy(
    approvals.map((a) => a.submittedJobBill.client),
    "id",
  );

  const approvalsByClient = _(uniqueClients)
    .map((client) => ({
      client,
      items: approvalsByClientId[client.id] || [],
    }))
    .sortBy(({ client }) => client.name.toLowerCase())
    .value();

  const exceptionByClientId = keyBy(details?.clientExceptions, "clientId");

  const totalBilledAmount = sumBy(
    details?.commissionApprovals,
    "thresholdMaker",
  );

  const thresholdTotal = getApprovalsTotal(
    approvals,
    details?.thresholdType,
    exceptionByClientId,
  );

  const getIconColorForClient = (client) => {
    const exceptionId = exceptionByClientId[client.id]?.id;
    return thresholdTotal.exceptionsTotal[exceptionId] ? "#26a4ff" : "#444";
  };

  const deleteCommission = async () => {
    try {
      await CommissionPlan.deleteCommissionStatement(id);
      history.goBack();
      message.success("Commission statement deleted successfully");
    } catch (err) {
      setPageData((prev) => ({ ...prev, isModalVisible: false }));
      console.error(err);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await CommissionPlan.getCommissionStatementDetail(id);
        setPageData((prev) => ({
          ...prev,
          details: res,
        }));
      } catch (err) {
        console.error(err);
      }
    })();
  }, [id]);

  const getFormattedThreshold = (thresholdType, defaultValue, totalBilled) => {
    const defaultToDisplay = getThresholdValue(thresholdType, defaultValue);
    const totalToDisplay = getThresholdValue(thresholdType, totalBilled);

    return `${defaultToDisplay} / ${totalToDisplay}`;
  };

  return (
    <>
      <div className="pdf-wrap">
        <div className="pdf-header">
          <div className="pdf-heading">
            <Title level={5}>Source 2</Title>
          </div>
          <div className="pdf-header-img">
            <img src={PdfLogo} alt="" />
          </div>
        </div>
        <div className="report-list-wrap">
          <Paragraph className="report-para">
            941 W Morse Boulevard, Suite 100
            <br />
            Winter Park, FL 32789 US
            <br />
            accounting@source2.com
            <br /> www.source2.com
          </Paragraph>
          <div className="d-flex justify-content-between">
            <Title level={2}>Commission Statement</Title>
          </div>
        </div>
      </div>
      <div className={styles.wrapPageHeader}>
        <PageHeader
          className={LayoutCss.appPageHeader}
          ghost={false}
          onBack={() => window.history.back()}
          title="Statement Details"
          extra={
            isPermissionPresent(
              K.Permissions.DeleteCommissionStatement,
              userSlice.roles,
            ) && (
              <div className={styles.invoiceModal}>
                <Popconfirm
                  placement="bottom"
                  title="Are you sure you want to delete this commission statement?"
                  onConfirm={deleteCommission}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button style={{ marginLeft: 10 }} type="primary" danger>
                    Delete
                  </Button>
                </Popconfirm>
              </div>
            )
          }
        />
      </div>
      <div className={`${styles.topInfoCard} ${styles.statementDetails}`}>
        <Card className={styles.generateInvoiceCard}>
          <div className={styles.topCardDetail}>
            <div className={styles.cardBox} style={{ flexBasis: 500 }}>
              <ul>
                <li>
                  <span className={styles.cardIdDate}>Status:</span>
                  <span className={styles.cardIdDate}>
                    <Tag
                      color={tagColors[details?.status]}
                      style={{ margin: 0 }}
                    >
                      {details?.status
                        ? removeUnderScore(details.status)
                        : K.NullPlaceholder}
                    </Tag>
                  </span>
                </li>
                <li>
                  Payout Date:
                  <span>
                    {convertIntoDashUSFormat(details?.deliveryDate, false)}
                  </span>
                </li>
              </ul>

              <Divider style={{ margin: "16px 0" }} />

              <ul>
                <li>
                  Threshold / Total Billing
                  <div style={{ textAlign: "right" }}>
                    <span>
                      {getFormattedThreshold(
                        details?.thresholdType,
                        details?.defaultBillingThreshold,
                        thresholdTotal?.total,
                      )}
                    </span>
                  </div>
                </li>

                {details?.clientExceptions?.map((ex) => (
                  <li key={ex.id}>
                    <span style={{ marginLeft: 16 }}> {ex.client.name}</span>
                    <div style={{ textAlign: "right" }}>
                      <span>
                        {getFormattedThreshold(
                          ex.thresholdType,
                          ex.defaultBillingThreshold,
                          thresholdTotal?.exceptionsTotal[ex.id] || 0,
                        )}
                      </span>
                    </div>
                  </li>
                ))}

                <li>
                  Total Billing
                  <div style={{ textAlign: "right" }}>
                    <span>{displayDollar(totalBilledAmount)}</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className={styles.cardRightSide}>
              <ul>
                <li className={`${styles.cardDetails} ${styles.clientBalance}`}>
                  Recruiter:
                </li>
                <li>{details?.recruiter?.name}</li>
                <li className={styles.cardDetails}>Payout:</li>
                <li
                  className={`${styles.cardDetails} ${styles.invoicesDetails}`}
                >
                  {details?.status === "NOT_ELIGIBLE"
                    ? displayDollar(0)
                    : displayDollar(details?.amount ?? 0)}
                </li>
                <div className={styles.invoicesPrint}>
                  <Button
                    type="link"
                    className={styles.cardDetails}
                    onClick={() => {
                      window.print();
                    }}
                  >
                    Print
                  </Button>
                </div>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      <Card className={styles.bottomInfoCard}>
        {approvalsByClient.map((st) => (
          <div style={{ marginBottom: 16 }} key={st.client.id}>
            <div className={styles.bottomCardList}>
              <div className={styles.bottomCardIcons}>
                <ul style={{ marginBottom: 20 }}>
                  <li>
                    <div className={styles.invoiceIcon}>
                      <UserOutlined
                        style={{
                          color: getIconColorForClient(st.client),
                        }}
                      />
                    </div>
                    <div className={styles.invoiceIconDetail}>
                      <p className={styles.listName}>Client</p>
                      <Title level={5} className={styles.listDes}>
                        {st.client.name}
                      </Title>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <Title className={styles.printClientName} level={5}>
              {st.client.name}
            </Title>
            <Table
              columns={columns}
              dataSource={st.items}
              rowKey={(r) => r.id}
              size="small"
              pagination={false}
              className="invoiceTable"
              summary={renderSummary}
            />
          </div>
        ))}
      </Card>
    </>
  );
}
